import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import ProductCard from "./ProductCard";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";

const RELATED_LIMIT = 12;

async function loadRelated({ slug, productId, categorySlug }) {
  const seen = new Set([productId]);
  const out = [];

  const push = (list) => {
    for (const item of list || []) {
      if (!item?.id || seen.has(item.id)) continue;
      seen.add(item.id);
      out.push(item);
      if (out.length >= RELATED_LIMIT) return true;
    }
    return false;
  };

  if (categorySlug) {
    const { data } = await api.get(`/store/${slug}/products`, { params: { category: categorySlug } });
    if (push(data)) return out;
  }
  if (out.length < 4) {
    const { data } = await api.get(`/store/${slug}/products`);
    push(data);
  }
  return out;
}

export default function RelatedProductSlider({ slug, productId, categorySlug, categoryName, categoryPending, viewAllHref, primary, variant = "default" }) {
  const [items, setItems] = useState([]);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!slug || !productId) return;
    if (categoryPending) return;
    let alive = true;
    setReady(false);
    loadRelated({ slug, productId, categorySlug })
      .then((list) => { if (alive) setItems(list); })
      .catch(() => { if (alive) setItems([]); })
      .finally(() => { if (alive) setReady(true); });
    return () => { alive = false; };
  }, [slug, productId, categorySlug, categoryPending]);

  if (!ready || items.length === 0) return null;

  return (
    <section className="mt-14 pt-10 border-t border-stone-200" data-testid="related-products">
      <div className="flex items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold" style={{ color: primary }}>You may also like</div>
          <h2 className="mt-2 font-display text-2xl sm:text-3xl font-bold text-stone-900">
            {categoryName ? `More from ${categoryName}` : "Related products"}
          </h2>
        </div>
        {viewAllHref && (
          <Link to={viewAllHref} className="text-sm font-semibold hover:underline underline-offset-4 shrink-0" style={{ color: primary }} data-testid="related-view-all">
            View all
          </Link>
        )}
      </div>

      <Carousel
        opts={{ align: "start", dragFree: true, containScroll: "trimSnaps" }}
        className="relative px-1"
      >
        <CarouselContent className="-ml-3">
          {items.map((item) => (
            <CarouselItem
              key={item.id}
              className="pl-3 basis-[72%] sm:basis-1/2 md:basis-1/3 lg:basis-1/4"
              data-testid={`related-slide-${item.slug}`}
            >
              <ProductCard p={item} slug={slug} variant={variant} />
            </CarouselItem>
          ))}
        </CarouselContent>
        <CarouselPrevious
          data-testid="related-prev"
          className="left-0 md:-left-4 border-stone-200 bg-white/95 shadow-md disabled:opacity-0"
        />
        <CarouselNext
          data-testid="related-next"
          className="right-0 md:-right-4 border-stone-200 bg-white/95 shadow-md disabled:opacity-0"
        />
      </Carousel>
    </section>
  );
}
